import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import * as wjGrid from "wijmo/wijmo.grid";
import * as wjCore from "wijmo/wijmo";
import { WjFlexGrid } from "wijmo/wijmo.angular2.grid";
import * as wjXlsx from "wijmo/wijmo.grid.xlsx";
import { ConvertIsoDateService } from "@app/modules/shared/services/convert-iso-date.service";
import { ApiService } from "@app/modules/shared/services/base/api.service";
import { UtilService } from "@app/modules/shared/services/util.service";
import { KlSweetAlertService } from "@app/modules/shared/services/message/kl-sweet-alert.service";
import { KlPreferenceSaveFormComponent } from "@app/modules/shared/components/preference-form/kl-preference-save-form.component";
import * as wjInput from "wijmo/wijmo.input";
import { ExplorerService } from "@app/modules/shared/services/explorer.service";
import { Subscription } from "rxjs/Subscription";

interface ColumnInterface {
  visible?: boolean;
  header?: string;
  name?: string;
  binding?: string;
  dataMap?: any;
  showDropDown?: string;
  index?: boolean;
  isReadOnly?: boolean;
  inputReadOnly?: boolean;
  request_api?: boolean;
  isRequired?: boolean;
  translate?: boolean;
  generateCode?: boolean;
  generateCodeSpin?: boolean;
  width?: any;
  cssClass?: string;
  format?: string;
  maxLength?: number;
  max?: number;
  min?: number;
  action?: any;
  buttonAction?: any;
  icon?: string;
}

@Component({
  selector: "kl-form-entry-grid",
  templateUrl: "./form-entry-grid.component.html",
  styleUrls: ["./form-entry-grid.component.scss"],
})
export class FormEntryGridComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild("flexGrid", { static: true }) private flexGrid: wjGrid.FlexGrid;
  @ViewChild("formGridMenu", { static: true }) formGridMenu: any;
  @ViewChild("gridElement", { static: true }) gridElement: ElementRef;
  @ViewChild(forwardRef(() => KlPreferenceSaveFormComponent), { static: true })
  klPreferenceSaveFormComponent: any;

  // Inputs
  @Input() actions: Array<{
    method: Function;
    show: boolean;
    slug: string;
    children: Array<object>;
  }>;
  @Input() collectionView: wjCore.CollectionView;
  @Input() columns: Array<ColumnInterface>;
  @Input() viewableRowCount: number;
  @Input() selectionColumn: number;
  @Input() allowResizing: any;
  @Input() allowAddNew = true;
  @Input() allowDelete = true;
  @Input() trackingChanges = true;
  @Input() loading: boolean;
  @Input() showErrors: boolean;
  @Input() validateEdits: boolean;
  @Input() deferResizing: boolean;
  @Input() setMaxWidth: boolean;
  @Input() isReadOnly: boolean;
  @Input() actionButton: boolean;
  @Input() formName: string;
  @Input() gridName: string;
  @Input() parentModuleName: string;
  @Input() moduleName: string;

  // Outputs
  @Output() onColumnChange = new EventEmitter<any>();
  @Output() onComboBoxChange = new EventEmitter<any>();
  @Output() onCellEdit = new EventEmitter<any>();
  @Output() onLostFocus = new EventEmitter<any>();
  @Output() onAddNew = new EventEmitter<any>();
  @Output() onDelete = new EventEmitter<any>();
  @Output() onRowChange = new EventEmitter<any>();
  @Output() onItemsSourceChanged = new EventEmitter<any>();
  @Output() onRowEditEnded = new EventEmitter<any>();
  reader = new FileReader();
  currentItem: any;
  newItem: any;
  pastedRowIndex: number;
  selectionRowIndex: number;
  currentRow = 0;
  currentCol = 0;
  editing = false;
  isTemplatesOpen = false;
  isSidebarShow = false;
  cellEditCondition = false;
  generateCodeSpin = false;
  deletable = false;

  visibleColumns = [];

  currentColumnData: any;
  defaultTemplate: any;
  currentTemplate: any;
  columnLayout = [];
  defaultColumns = [];
  layout: any;
  index: number;
  templates = [];
  layoutId: string;

  alertType: string;
  alertMessage: string;
  alertShow: boolean;

  finishInit = false;
  subscription: Subscription;

  @HostListener("window:resize", ["$event"]) onResize(event) {
    const formGrid = document.getElementById(
      this.gridName ? this.gridName : "form-grid"
    );
    const formContainer = document.getElementById("form-container");
    if (this.setMaxWidth) {
      formGrid.style.maxWidth =
        this.convertString(formContainer.clientWidth - 20) + "px";
    }
    this.flexGrid.refresh(true);
  }

  constructor(
    private dateConvert: ConvertIsoDateService,
    private sweetAlertService: KlSweetAlertService,
    private utilService: UtilService,
    private explorerService: ExplorerService,
    private apiService: ApiService
  ) {
    this.subscription = explorerService.menuCollapseStream$.subscribe(() => {
      if (this.flexGrid) {
        this.flexGrid.refresh(true);
      }
    });
  }

  ngOnInit() {
    this.flexGrid.rows.defaultSize = 30;
    this.flexGrid.columnHeaders.rows.defaultSize = 30;
    this.flexGrid.selectionChanged.addHandler((s, e: any) => {
      if (this.selectionRowIndex !== e.row) {
        this.onRowChange.emit({
          row: e.row,
          flexGrid: s,
        });
      }
    });
    this.flexGrid.selectionChanging.addHandler((s) => {
      this.selectionRowIndex = s.selection.row;
    });
    this.flexGrid.pastedCell.addHandler((s, e: any) => {
      if (!isNaN(this.pastedRowIndex)) {
        if (this.pastedRowIndex !== e.row) {
          this.onAddNew.emit(s);
          this.onColumnChange.emit({ row: e.row, col: e.col, flexGrid: s });
          this.pastedRowIndex = e.row;
        }
      } else {
        this.onColumnChange.emit({
          row: e.row,
          col: e.col,
          flexGrid: this.flexGrid,
        });
        this.pastedRowIndex = e.row;
        this.onAddNew.emit(s);
      }
    });
    this.flexGrid.prepareCellForEdit.addHandler(
      (flexGrid: wjGrid.FlexGrid, event: any) => {
        let control;
        const rootEl = document.createElement("div");
        if (this.columns[event.col].action) {
          let elementI: any;
          let elementImg: any;
          const column = this.columns[event.col];
          if (!(event.panel.cellType === wjGrid.CellType.Cell)) {
            return;
          }
          rootEl.style.width = "100%";
          rootEl.style.height = "100%";
          const dataItem = flexGrid.rows[event.row].dataItem;
          control = document.createElement("div");
          let value = "";
          const input: any = document.createElement("Input");
          const buttonDiv: any = document.createElement("div");
          const span: any = document.createElement("span");
          control.className = "input-group";
          input.readOnly = column.inputReadOnly;
          input.type = "text";
          input.className = "form-control form-control-sm";
          span.className =
            "input-group-text" +
            (column.generateCode ? "" : " fal fa-ellipsis-h");
          buttonDiv.className = "input-group-append";
          if (column.generateCode) {
            elementI = document.createElement("i");
            elementImg = document.createElement("Img");
            elementI.className = "fal fa-spinner fa-spin fa-fw";
            elementI.hidden = true;
            elementImg.hidden = false;
            elementImg.src = "assets/img/keys.svg";
            elementImg.style.width = "15px";
            elementImg.style.margin = "auto";
            elementI.style.margin = "auto";
            span.style.width = "30px";
            span.style.padding = "0";
            span.style.textAlign = "center";
            span.appendChild(elementI);
            span.appendChild(elementImg);
          }
          value = UtilService.deepCopy(
            flexGrid.getCellData(event.row, event.col, false)
              ? UtilService.deepCopy(
                  flexGrid.getCellData(event.row, event.col, false)
                )
              : ""
          );
          buttonDiv.addEventListener("click", (clickEvent) => {
            if (column.generateCode) {
              elementI.hidden = false;
              elementImg.hidden = true;
              const returnedData = this.columns[event.col].action.method({
                item: dataItem,
                row: flexGrid.rows[event.row],
              });
              this.apiService
                .addData(returnedData.endpoint, returnedData.filter)
                .finally(() => {
                  elementI.hidden = true;
                  elementImg.hidden = false;
                })
                .subscribe(
                  (resCode) => {
                    input.value = resCode.document_number;
                    flexGrid.setCellData(
                      event.row,
                      event.col,
                      input.value,
                      false
                    );
                  },
                  (errCode) => this.handleError(errCode)
                );
            } else {
              this.columns[event.col].action.method({
                item: dataItem,
                row: flexGrid.rows[event.row],
              });
            }
            clickEvent.preventDefault();
            clickEvent.stopImmediatePropagation();
          });
          buttonDiv.appendChild(span);
          control.appendChild(input);
          control.appendChild(buttonDiv);
          rootEl.appendChild(control);
          if (flexGrid.activeEditor) {
            const parentCell = flexGrid.activeEditor.parentElement;
            parentCell.style.padding = "0";
            parentCell.insertBefore(rootEl, flexGrid.activeEditor);
            control.children[0].focus();
            if (!wjCore.isUndefined(control["text"])) {
              control.children[0]["text"] = flexGrid.activeEditor.value;
            }
          }
          if (!column.inputReadOnly) {
            const cellEditEndHandler = (flexGrid_, args) => {
              flexGrid_.cellEditEnding.removeHandler(cellEditEndHandler);
              if (!args.cancel) {
                args.cancel = true;
                flexGrid_.setCellData(args.row, args.col, input.value, false);
                flexGrid.columns[event.col].isReadOnly = false;
                if (control.children[0].dispose) {
                  control.children[0].dispose();
                }
              }
            };
            flexGrid.cellEditEnding.addHandler(cellEditEndHandler);
          }
          setTimeout(() => {
            input.value = value;
          }, 1);
        } else if (
          this.columns[event.col].format === "numeric" ||
          this.columns[event.col].format === "password"
        ) {
          control = document.createElement("Input");
          control.type = this.columns[event.col].format;
          control.maxLength = this.columns[event.col].maxLength;
          control.className = "form-control form-control-sm";
          control.value = flexGrid.getCellData(event.row, event.col, false);
          if (this.columns[event.col].format === "numeric") {
            control.addEventListener("paste", (pasteEvent) => {
              const numberArray = [
                "0",
                "1",
                "2",
                "3",
                "4",
                "5",
                "6",
                "7",
                "8",
                "9",
              ];
              const target = pasteEvent.target;
              target.maxLength = 100;
              setTimeout(() => {
                let finallyValue = "";
                for (let i = 0; i < target.value.length; i++) {
                  const validNumber = numberArray.find(
                    (result) => target.value[i] === result
                  );
                  if (validNumber) {
                    if (
                      finallyValue.length < this.columns[event.col].maxLength
                    ) {
                      finallyValue += target.value[i];
                    }
                  }
                }
                target.value = finallyValue;
                target.maxLength = this.columns[event.col].maxLength;
              }, 1);
            });
            control.addEventListener(
              "keypress",
              (keyboardEvent: KeyboardEvent) => {
                const code = keyboardEvent.charCode
                  ? keyboardEvent.charCode
                  : keyboardEvent.which
                  ? keyboardEvent.which
                  : keyboardEvent.keyCode;
                control.maxLength = this.columns[event.col].maxLength;
                if (code < 48 || code > 57) {
                  if (
                    code === 8 ||
                    code === 46 ||
                    code === 9 ||
                    (code > 1775 && code < 1786)
                  ) {
                  } else {
                    keyboardEvent.preventDefault();
                  }
                }
              }
            );
          }
          rootEl.appendChild(control);
          if (flexGrid.activeEditor) {
            const parentCell = flexGrid.activeEditor.parentElement;
            parentCell.style.padding = "0";
            parentCell.insertBefore(rootEl, flexGrid.activeEditor);
            control.focus();
            if (!wjCore.isUndefined(control["text"])) {
              control["text"] = flexGrid.activeEditor.value;
            }
          }
          const cellEditEndHandler = (flexGrid_, args) => {
            flexGrid_.cellEditEnding.removeHandler(cellEditEndHandler);
            if (!args.cancel) {
              args.cancel = true;
              flexGrid_.setCellData(args.row, args.col, control.value, false);
              if (control.dispose) {
                control.dispose();
              }
            }
          };
          flexGrid.cellEditEnding.addHandler(cellEditEndHandler);
        } else if (
          this.columns[event.col].format === "n2" ||
          this.columns[event.col].format === "n5" ||
          this.columns[event.col].format === "n0"
        ) {
          rootEl.style.width = "100%";
          rootEl.style.height = "100%";
          control = new wjInput.InputNumber(rootEl, {
            min: this.columns[event.col].min ? this.columns[event.col].min : 1,
            max: this.columns[event.col].max ? this.columns[event.col].max : 1,
            showSpinner: false,
            format: this.columns[event.col].format,
          });
          control.value = Number(
            flexGrid.getCellData(event.row, event.col, false)
          );
          if (flexGrid.activeEditor) {
            const parentCell = flexGrid.activeEditor.parentElement;
            parentCell.style.padding = "0";
            parentCell.insertBefore(rootEl, flexGrid.activeEditor);
            control.focus();
            if (!wjCore.isUndefined(control["text"])) {
              control["text"] = flexGrid.activeEditor.value;
            }
          }
          const cellEditEndHandler = (flexGrid_, args) => {
            flexGrid_.cellEditEnding.removeHandler(cellEditEndHandler);
            if (!args.cancel) {
              args.cancel = true;
              flexGrid_.setCellData(args.row, args.col, control.value, false);
              if (control.dispose) {
                control.dispose();
              }
            }
          };
          flexGrid.cellEditEnding.addHandler(cellEditEndHandler);
        }
      }
    );
    this.flexGrid.addEventListener(
      this.flexGrid.hostElement,
      "contextmenu",
      (e: any) => {
        if (
          !this.isReadOnly &&
          !this.cellEditCondition &&
          this.collectionView.items.length > 0 &&
          this.allowDelete
        ) {
          const ht = this.flexGrid.hitTest(e);
          this.currentItem =
            ht.row >= 0
              ? this.flexGrid.rows[ht.row].dataItem
              : this.flexGrid.selectedItems[0];
        } else {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true
    );
    if (this.moduleName) {
      this.defaultColumns = [...this.columns];
      this.apiService
        .getAllData(
          "design/parent/" +
            this.parentModuleName +
            "/" +
            this.moduleName +
            "/" +
            this.gridName
        )
        .subscribe(
          (resPreference) => {
            if (resPreference.length > 0) {
              for (let i = 0; i < resPreference.length; i++) {
                if (resPreference[i].level_default === "DEFAULT") {
                  this.currentTemplate = {
                    layout: JSON.parse(resPreference[i].content),
                    description: resPreference[i].description,
                    id: resPreference[i].id,
                    level: resPreference[i].level,
                    level_default: resPreference[i].level_default,
                  };
                  this.defaultTemplate = JSON.parse(
                    resPreference[i].content
                  ).columns;
                  this.columns = [...this.defaultTemplate];
                  this.flexGrid.refresh(true);
                }
                this.templates.push({
                  layout: JSON.parse(resPreference[i].content),
                  description: resPreference[i].description,
                  id: resPreference[i].id,
                  level: resPreference[i].level,
                  level_default: resPreference[i].level_default,
                });
              }
            }
          },
          (errPreference) => {
            this.alertMessage =
              this.utilService.alertErrorMessage(errPreference);
            this.alertType = "error";
            this.alertShow = true;
          }
        );
    }
  }

  ngAfterViewInit() {
    let longClickTimer,
      longPress = false;
    for (let i = 0; i < this.flexGrid.columns.length; i++) {
      if (this.flexGrid.columns[i].visible) {
        this.visibleColumns.push(this.flexGrid.columns[i]);
      }
    }
    this.flexGrid.hostElement.addEventListener(
      "touchstart",
      (e: TouchEvent) => {
        const self = this,
          userAgent = window.navigator.userAgent;
        if (userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)) {
          longClickTimer = setTimeout(() => {
            longPress = true;
          }, 500);
        }
      },
      true
    );
    this.flexGrid.hostElement.addEventListener(
      "touchend",
      (e) => {
        clearTimeout(longClickTimer);
      },
      true
    );
    this.flexGrid.hostElement.addEventListener("click", (e) => {
      const contextMenu = <wjInput.Menu>(
        wjCore.Control.getControl("#form-grid-context-menu")
      );
      if (
        longPress &&
        !this.isReadOnly &&
        this.collectionView.items.length > 0
      ) {
        longPress = false;
        let ht: wjGrid.HitTestInfo;
        ht = this.flexGrid.hitTest(e);
        if (ht && ht.cellType === wjGrid.CellType.Cell) {
          this.currentItem =
            ht.row >= 0
              ? this.flexGrid.rows[ht.row].dataItem
              : this.flexGrid.selectedItems[0];
          contextMenu.isDroppedDown = true;
          const contextMenuList = <HTMLDivElement>(
            document.querySelector("#form-grid-context-menu_dropdown")
          );
          this.showContextMenu(
            contextMenuList,
            e,
            new wjCore.Point(e.pageX + 10, e.pageY + 10)
          );
        }
      } else {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    });
  }

  makePasswordStar(value) {
    let star = "";
    for (let i = 0; i < value.length; i++) {
      star = star + "·";
    }
    return star;
  }

  menuItemClicked(formGridMenu, flexGrid) {
    switch (formGridMenu.value) {
      case "delete":
        this.deleteRow(flexGrid, this.currentItem);
        break;
      default:
        this.actions.forEach((action) => {
          if (action.slug === formGridMenu.value) {
            action.method({ item: this.currentItem });
          }
        });
        break;
    }
  }

  private showContextMenu(
    contextMenu: HTMLElement,
    e: any,
    point: wjCore.Point
  ) {
    let posX = (point ? point.x : e.clientX) + (e ? window.pageXOffset : 0),
      posY = (point ? point.y : e.clientY) + (e ? window.pageYOffset : 0);
    contextMenu.style.position = "absolute";
    contextMenu.style.display = "inline";
    if (posY + contextMenu.clientHeight > window.innerHeight) {
      posY -= contextMenu.clientHeight;
    }
    if (posX + contextMenu.clientWidth > window.innerWidth) {
      posX -= contextMenu.clientWidth;
    }
    contextMenu.style.top = posY + "px";
    contextMenu.style.left = posX + "px";
  }

  deleteLayout(template, index) {
    this.sweetAlertService.alert(
      {
        title: "Uyarı",
        text: "Kayıt Silinecektir",
        customClass: "kl-alert",
        showCloseButton: false,
      },
      () => {
        this.apiService.deleteData("design", template.id).subscribe(
          (resPreference) => {
            this.templates.splice(index, 1);
            this.setDefaultTemplate();
          },
          (errPreference) => {
            this.alertMessage =
              this.utilService.alertErrorMessage(errPreference);
            this.alertType = "error";
            this.alertShow = true;
          }
        );
      },
      () => {}
    );
  }

  setDefaultTemplate() {
    this.currentTemplate = null;
    this.columns = [...this.defaultColumns];
    this.flexGrid.refresh(true);
  }

  selectLayout(template, index) {
    this.currentTemplate = template;
    this.index = index;
    this.currentColumnData = [];
    template.layout.columns.forEach((column) => {
      for (let i = 0; i < this.defaultColumns.length; i++) {
        if (column.binding === this.defaultColumns[i].binding) {
          const obj = Object.assign(this.defaultColumns[i], {
            visible: !column.visible,
          });
          this.currentColumnData.push(obj);
        }
      }
    });
    this.columns = [];
    this.columns = [...this.currentColumnData];
    this.flexGrid.refresh(true);
  }

  saveLayout(mode, currentTemplate?) {
    this.layoutId = currentTemplate ? currentTemplate.id : null;
    this.klPreferenceSaveFormComponent.show(
      mode,
      "design",
      currentTemplate ? currentTemplate : null
    );
  }

  commitSave(event: any) {
    let commitObject = Object.create(null);
    this.columnLayout = [];
    const columns = [];
    for (let i = 0; i < this.flexGrid.columns.length; i++) {
      for (let j = 0; j < this.defaultColumns.length; j++) {
        if (
          this.flexGrid.columns[i].binding === this.defaultColumns[j].binding
        ) {
          columns.push({
            binding: this.defaultColumns[j].binding,
            visible: this.flexGrid.columns[i].visible,
          });
        }
      }
    }
    commitObject = {
      parent: {
        module: this.parentModuleName,
      },
      level: event.levelStatus ? "APPLICATION" : "USER",
      level_default: event.levelDesign ? "DEFAULT" : "NO_DEFAULT",
      description: event.entryName,
      module: this.moduleName,
      key: this.gridName,
      type: "GRID_DESIGN",
      content: JSON.stringify({ columns: columns }),
    };
    switch (event.commitType) {
      case "add":
        this.apiService.addData("design", commitObject).subscribe(
          (resPreference) => {
            this.currentTemplate = {
              layout: JSON.parse(resPreference.content),
              description: resPreference.description,
              id: resPreference.id,
              level: resPreference.level,
              level_default: resPreference.level_default,
            };
            this.templates.push({
              layout: JSON.parse(resPreference.content),
              description: resPreference.description,
              id: resPreference.id,
              level: resPreference.level,
              level_default: resPreference.level_default,
            });
            this.klPreferenceSaveFormComponent.hide();
          },
          (errPreference) => {
            this.alertMessage =
              this.utilService.alertErrorMessage(errPreference);
            this.alertType = "error";
            this.alertShow = true;
          }
        );
        break;
      case "edit":
        commitObject["id"] = this.layoutId;
        this.apiService.updateData("design", commitObject).subscribe(
          (resPreference) => {
            this.templates[this.index].description = resPreference.description;
            this.templates[this.index].layout = JSON.parse(
              resPreference.content
            );
            this.templates[this.index].id = resPreference.id;
            this.templates[this.index].level = resPreference.level;
            this.templates[this.index].level_default =
              resPreference.level_default;
            this.klPreferenceSaveFormComponent.hide();
          },
          (errPreference) => {
            this.alertMessage =
              this.utilService.alertErrorMessage(errPreference);
            this.alertType = "error";
            this.alertShow = true;
          }
        );
        break;
    }
  }

  columnLostFocus(flexGrid: WjFlexGrid, index) {
    flexGrid.lostFocus.addHandler((s, e: any) => {
      if (flexGrid.rows[e.row]) {
        this.cellEditCondition = false;
        if (Object.keys(flexGrid.rows[e.row].dataItem).length > 0) {
          if (flexGrid.columns[e.col].binding.includes(".")) {
            const prepare = flexGrid.columns[e.col].binding.split(".");
            this.newItem = flexGrid.rows[e.row].dataItem[prepare[0]][prepare[1]]
              ? flexGrid.rows[e.row].dataItem[prepare[0]][prepare[1]]
              : "";
          } else {
            this.newItem = flexGrid.rows[e.row].dataItem[
              flexGrid.columns[e.col].binding
            ]
              ? flexGrid.rows[e.row].dataItem[flexGrid.columns[e.col].binding]
              : "";
          }
          if (this.currentItem !== this.newItem) {
            this.onColumnChange.emit({
              row: e.row,
              col: e.col,
              flexGrid: flexGrid,
            });
            this.deletable = false;
          }
          this.flexGrid.refresh(true);
        }
      }
    });
  }

  gridLostFocus(flexGrid: WjFlexGrid) {
    if (this.cellEditCondition) {
      this.onLostFocus.emit({
        row: this.currentRow,
        col: this.currentCol,
        flexGrid: flexGrid,
      });
      this.cellEditCondition = false;
    }
  }

  cellEdit(flexGrid: WjFlexGrid, event: wjGrid.CellEditEndingEventArgs) {
    this.cellEditCondition = true;
    this.deletable = true;
    this.currentCol = event.col;
    this.currentRow = event.row;
    this.onCellEdit.emit({
      flexGrid: flexGrid,
      event: event,
    });
  }

  columnChange(flexGrid: WjFlexGrid) {
    if (!this.finishInit) {
      const formGrid = document.getElementById(
        this.gridName ? this.gridName : "form-grid"
      );
      flexGrid.hostElement.style.maxHeight =
        this.convertString(
          27 + 32 * (this.viewableRowCount ? this.viewableRowCount : 10)
        ) + "px";
      flexGrid.hostElement.style.overflow = "auto";
      if (this.setMaxWidth) {
        flexGrid.hostElement.style.maxWidth =
          this.convertString(formGrid.clientWidth) + "px";
      }
      this.finishInit = true;
    }
    if (this.trackingChanges) {
      if (!this.isReadOnly) {
        flexGrid.cellEditEnded.addHandler((s, e: any) => {
          this.deletable = false;
          if (flexGrid.rows[e.row]) {
            this.cellEditCondition = false;
            if (Object.keys(flexGrid.rows[e.row].dataItem).length > 0) {
              if (flexGrid.columns[e.col].binding.includes(".")) {
                const prepare = flexGrid.columns[e.col].binding.split(".");
                if (flexGrid.rows[e.row].dataItem[prepare[0]]) {
                  this.newItem = flexGrid.rows[e.row].dataItem[prepare[0]][
                    prepare[1]
                  ]
                    ? flexGrid.rows[e.row].dataItem[prepare[0]][prepare[1]]
                    : "";
                }
              } else {
                this.newItem = flexGrid.rows[e.row].dataItem[
                  flexGrid.columns[e.col].binding
                ]
                  ? flexGrid.rows[e.row].dataItem[
                      flexGrid.columns[e.col].binding
                    ]
                  : "";
              }
              if (this.currentItem !== this.newItem) {
                this.onColumnChange.emit({
                  row: e.row,
                  col: e.col,
                  flexGrid: flexGrid,
                });
              }
              this.flexGrid.refresh(true);
            }
          }
        });
        flexGrid.cellEditEnding.addHandler((s, e: any) => {
          if (flexGrid.rows[e.row]) {
            this.deletable = true;
            if (Object.keys(flexGrid.rows[e.row].dataItem).length > 0) {
              if (flexGrid.columns[e.col].binding.includes(".")) {
                const prepare = flexGrid.columns[e.col].binding.split(".");
                if (flexGrid.rows[e.row].dataItem[prepare[0]]) {
                  this.currentItem = flexGrid.rows[e.row].dataItem[prepare[0]][
                    prepare[1]
                  ]
                    ? flexGrid.rows[e.row].dataItem[prepare[0]][prepare[1]]
                    : "";
                }
              } else {
                this.currentItem = flexGrid.rows[e.row].dataItem[
                  flexGrid.columns[e.col].binding
                ]
                  ? flexGrid.rows[e.row].dataItem[
                      flexGrid.columns[e.col].binding
                    ]
                  : "";
              }
            }
          }
        });
      }
    }
  }

  firstRow(event, flexGrid) {
    const selectedCellRange = flexGrid.selection;
    if (flexGrid.collectionView.sourceCollection.length > 0) {
      const currentRow = flexGrid.selectedRows[0]
        ? flexGrid.selectedRows[0].index
        : 0;
      selectedCellRange.row = currentRow;
      selectedCellRange.col = this.selectionColumn ? this.selectionColumn : 0;
      selectedCellRange.row2 = selectedCellRange.row;
      selectedCellRange.col2 = this.selectionColumn ? this.selectionColumn : 0;
      flexGrid.select(selectedCellRange, true);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  endRow(event, flexGrid) {
    const endColumnIndex = flexGrid.columns.length;
    const selectedCellRange = flexGrid.selection;
    if (flexGrid.collectionView.sourceCollection.length > 0) {
      const currentRow = flexGrid.selectedRows[0]
        ? flexGrid.selectedRows[0].index
        : flexGrid.collectionView.sourceCollection.length - 1;
      selectedCellRange.row = currentRow;
      selectedCellRange.col = endColumnIndex - 1;
      selectedCellRange.row2 = selectedCellRange.row;
      selectedCellRange.col2 = endColumnIndex - 1;
      flexGrid.select(selectedCellRange, true);
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  keyDown(flexGrid: wjGrid.FlexGrid, event: any) {
    if (!this.isReadOnly) {
      const keyCode = window.event ? event.which : event.keyCode;
      const selectedCellRange = flexGrid.selection;
      if (keyCode === 13 || keyCode === 9) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        if (flexGrid.selection.col !== this.visibleColumns.length - 1) {
          if (keyCode === 13) {
            if (!flexGrid.columns[flexGrid.selection.col].isReadOnly) {
              if (flexGrid.activeEditor) {
                flexGrid.endUpdate();
                selectedCellRange.col = selectedCellRange.col + 1;
                selectedCellRange.row2 = selectedCellRange.row;
                selectedCellRange.col2 = selectedCellRange.col;
                flexGrid.select(selectedCellRange, true);
                if (
                  flexGrid.columns[flexGrid.selection.col].isReadOnly &&
                  flexGrid.selection.col < this.visibleColumns.length - 1
                ) {
                  selectedCellRange.col = selectedCellRange.col + 1;
                  selectedCellRange.col2 = selectedCellRange.col;
                }
                if (!this.columns[selectedCellRange.col].request_api) {
                  flexGrid.startEditing(true, event.row, event.col, true);
                }
              } else {
                flexGrid.startEditing(true, event.row, event.col, true);
              }
            } else {
              selectedCellRange.col = selectedCellRange.col + 1;
              selectedCellRange.row2 = selectedCellRange.row;
              selectedCellRange.col2 = selectedCellRange.col;
              flexGrid.select(selectedCellRange, true);
              if (!this.loading) {
                flexGrid.startEditing(true, event.row, event.col, true);
              }
            }
          } else {
            selectedCellRange.col = selectedCellRange.col + 1;
            selectedCellRange.row2 = selectedCellRange.row;
            selectedCellRange.col2 = selectedCellRange.col;
            flexGrid.select(selectedCellRange, true);
          }
        } else if (flexGrid.selection.col === this.visibleColumns.length - 1) {
          if (
            flexGrid.selection.row === flexGrid.rows.length - 1 &&
            (keyCode === 13 || keyCode === 9) &&
            this.allowAddNew
          ) {
            this.onAddNew.emit(flexGrid);
          }
          selectedCellRange.row = selectedCellRange.row + 1;
          selectedCellRange.col = this.selectionColumn
            ? this.selectionColumn
            : 0;
          selectedCellRange.row2 = selectedCellRange.row;
          selectedCellRange.col2 = this.selectionColumn
            ? this.selectionColumn
            : 0;
          flexGrid.select(selectedCellRange, true);
        }
      } else if (keyCode === 46 || keyCode === 8) {
        if (!this.deletable && this.allowDelete) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
        }
      } else if (keyCode === 40) {
        if (
          flexGrid.selection.row === flexGrid.rows.length - 1 &&
          this.allowAddNew
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
          this.onAddNew.emit(flexGrid);
          selectedCellRange.row = selectedCellRange.row + 1;
          selectedCellRange.col = this.selectionColumn
            ? this.selectionColumn
            : 0;
          selectedCellRange.row2 = selectedCellRange.row;
          selectedCellRange.col2 = this.selectionColumn
            ? this.selectionColumn
            : 0;
          flexGrid.select(selectedCellRange, true);
        }
      }
    }
  }

  deleteRow(flexGrid: wjGrid.FlexGrid, cell) {
    if (!this.isReadOnly && flexGrid.rows.length > 0 && this.allowDelete) {
      const currentRow = flexGrid.selectedRows[0]
        ? flexGrid.selectedRows[0].index
        : 0;
      this.collectionView.remove(cell);
      this.onDelete.emit({
        item: cell,
        row: currentRow,
      });
    }
  }

  export(type) {
    if (type === "XLSX") {
      wjXlsx.FlexGridXlsxConverter.save(
        this.flexGrid,
        {
          includeColumnHeaders: false,
          includeCellStyles: false,
        },
        this.dateConvert.convertDate(new Date()) +
          "-" +
          (this.formName ? this.formName : "excel") +
          ".xlsx"
      );
    }
  }

  convertString(value: number) {
    return value.toString();
  }

  openSidebar() {
    this.isSidebarShow = true;
  }

  handleError(error) {
    this.alertShow = true;
    this.alertType = "error";
    this.alertMessage = this.utilService.alertErrorMessage(error);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
